import cn from "classnames";
import { useState, useCallback, useEffect } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FCX } from "@datassential/platform-ui-lib";
import { PopularityTrendGapKeyword } from "@apiHooks/useCategoryProfilePopularityTrendGap";
import { MenuGapsEnum } from "@pages/CategoryProfile/sections/Menu/Menu.constants";
import { INNOVATION_COLUMN_WIDTH, VIEW_CONFIG, Y_AXIS_WIDTH } from "./PopularityTrendGapChart.constants";
import { PlottableKeyword } from "./PopularityTrendGapChart.helpers";
import PopularityTrendGapInnovationScale from "./PopularityTrendGapInnovationScale";
import PopularityTrendGapScatter from "./PopularityTrendGapScatter";
import PopularityTrendGapHoverTooltip, { ActiveDot } from "./PopularityTrendGapHoverTooltip";
import "./PopularityTrendGapChart.scss";

interface Props {
    keywords: PopularityTrendGapKeyword[];
    activeView: MenuGapsEnum;
}

const PopularityTrendGapChart: FCX<Props> = ({
    keywords,
    activeView,
    className,
    testId,
}) => {
    const [activeDot, setActiveDot] = useState<ActiveDot | null>(null);

    useEffect(() => {
        setActiveDot(null);
    }, [activeView, keywords]);

    const config = VIEW_CONFIG[activeView];

    const handleDotHover = useCallback(
        (kw: PlottableKeyword | null, cx?: number, cy?: number) =>
            setActiveDot(kw && cx !== undefined && cy !== undefined ? { kw, cx, cy } : null),
        [],
    );

    return (
        <div
            className={cn("PopularityTrendGapChart", className)}
            data-testid={testId}
        >
            {/* Green callout band (shown in both views so their heights match). */}
            {config.calloutText && (
                <p className="PopularityTrendGapChart__callout">
                    {config.calloutText}
                </p>
            )}

            {/* Plot region: y-axis label column + full-width plot */}
            <div className="PopularityTrendGapChart__plot-region">
                <div className="PopularityTrendGapChart__plot-container">
                    {/* Y-axis label column */}
                    <div className="PopularityTrendGapChart__y-axis" style={{ width: Y_AXIS_WIDTH }}>
                        <span className="PopularityTrendGapChart__axis-label">High</span>
                        <span className="PopularityTrendGapChart__axis-label-name">{config.yLabel}</span>
                        <span className="PopularityTrendGapChart__axis-label">Low</span>
                    </div>

                    {/* Plot area */}
                    <div className="PopularityTrendGapChart__plot-wrapper">
                        {/* The AutoSizer fills available width/height and passes px dimensions to the SVG layer. */}
                        <div className="PopularityTrendGapChart__svg-area">
                            <AutoSizer>
                                {({ width, height }: { width: number; height: number }) => (
                                    <>
                                        <PopularityTrendGapScatter
                                            keywords={keywords}
                                            config={config}
                                            width={width}
                                            height={height}
                                            onDotHover={handleDotHover}
                                        />
                                        <PopularityTrendGapHoverTooltip
                                            activeDot={activeDot}
                                            config={config}
                                            containerW={width}
                                            containerH={height}
                                        />
                                    </>
                                )}
                            </AutoSizer>
                        </div>

                        {/* X-axis labels */}
                        <div className="PopularityTrendGapChart__x-axis">
                            <span className="PopularityTrendGapChart__axis-label">Low</span>
                            <span className="PopularityTrendGapChart__axis-label-name">{config.xLabel}</span>
                            <span className="PopularityTrendGapChart__axis-label">High</span>
                        </div>
                    </div>
                </div>

                {/* Right-hand risk scale (Trend Gap) or an empty spacer (Popularity) so both views match in width. */}
                <div
                    className={cn("PopularityTrendGapChart__innovation-column", {
                        "PopularityTrendGapChart__innovation-column--empty": !config.showInnovationScale,
                    })}
                    style={{ width: INNOVATION_COLUMN_WIDTH }}
                    aria-hidden={config.showInnovationScale ? undefined : true}
                >
                    {config.showInnovationScale && <PopularityTrendGapInnovationScale />}
                </div>
            </div>
        </div>
    );
};

export default PopularityTrendGapChart;
